// Which language a person sees (brain/engineering/docs/white-label-and-i18n.md §4).
// Order: ?lang= in the address (links and tests), the person's choice on this device, the browser's
// languages, then English. A language never changes the time zone, region formats or billing country.

/** The languages the suite is built for. A locale is *released* only after native review (brand handbook 07). */
export const SUPPORTED_LOCALES = ['en', 'nl', 'de', 'fr'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const SOURCE_LOCALE: Locale = 'en';

/** Language names in their own language (never flags: a language is not a country). */
export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', nl: 'Nederlands', de: 'Deutsch', fr: 'Français' };

/** Languages written right to left. None is supported yet; the layout uses logical properties so one can be added. */
const RTL = new Set(['ar', 'he', 'fa', 'ur']);

export function localeDirection(locale: string): 'ltr' | 'rtl' {
  return RTL.has(locale.split('-')[0]?.toLowerCase() ?? '') ? 'rtl' : 'ltr';
}

/**
 * The best available locale for a list of requested ones: exact match first ("nl-NL" only if offered),
 * then the language ("nl-BE" → "nl"), else the fallback. Comparison ignores case and `_`.
 */
export function negotiateLocale(requested: readonly string[], available: readonly string[] = SUPPORTED_LOCALES, fallback: string = SOURCE_LOCALE): string {
  const norm = (s: string) => s.trim().replace(/_/g, '-').toLowerCase();
  const avail = available.map(norm);
  for (const r of requested.map(norm).filter(Boolean)) {
    const exact = avail.indexOf(r);
    if (exact >= 0) return available[exact] ?? fallback;
    const lang = r.split('-')[0] ?? '';
    const byLang = avail.indexOf(lang);
    if (byLang >= 0) return available[byLang] ?? fallback;
  }
  return fallback;
}

const KEY = 'dz-locale';

/** The person's choice on this device (a convenience; the account setting comes later and wins). */
export function storedLocale(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function storeLocale(locale: string | null): void {
  try {
    if (locale) localStorage.setItem(KEY, locale);
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode: the choice lasts for this page only */
  }
}

/** Requested locales, most wanted first. */
export function requestedLocales(): string[] {
  const out: string[] = [];
  if (typeof location !== 'undefined') {
    const q = new URLSearchParams(location.search).get('lang');
    if (q) out.push(q);
  }
  const s = storedLocale();
  if (s) out.push(s);
  if (typeof navigator !== 'undefined') out.push(...(navigator.languages ?? [navigator.language]).filter(Boolean));
  return out;
}

/**
 * The locale for dates and numbers: the interface language with the browser's region when they share a
 * language ("en" + en-US → en-US, "nl" + nl-BE → nl-BE), else the language on its own (British English for
 * "en", the source convention). Dates never mix a Dutch month into an English sentence.
 */
export function regionalLocale(language: string, browser: readonly string[] = typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language])): string {
  const lang = language.split('-')[0]?.toLowerCase() ?? language;
  const match = browser.find((b) => b && b.split('-')[0]?.toLowerCase() === lang);
  return match ?? (lang === 'en' ? 'en-GB' : language);
}

let activeLanguage: string = SOURCE_LOCALE;

/** The active interface language (set by I18nProvider), for code outside React. */
export function activeLocale(): string {
  return activeLanguage;
}

export function setActiveLocale(locale: string): void {
  activeLanguage = locale;
}

/** The active locale for dates and numbers (see regionalLocale), for code outside React. */
export function formatLocale(): string {
  return regionalLocale(activeLanguage);
}

/** "Today", "Tomorrow", "Yesterday" in the active language (Intl, no catalogue), or null for other offsets. */
export function relativeDay(offset: number, locale: string = formatLocale()): string | null {
  if (offset < -1 || offset > 1) return null;
  const s = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(offset, 'day');
  return s.charAt(0).toLocaleUpperCase(locale) + s.slice(1);
}
